// Google Analytics (GA4) initialization via gtag.js
// Measurement ID is synced by the Google Analytics connector.
//
// Google Advanced Consent Mode: on the production host the tag always loads, but
// consent defaults to denied for every storage type before any visitor choice.
// While analytics_storage is denied Google receives only cookieless pings.

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

import { isProductionHost } from './env';
import { getConsent, type ConsentChoices } from './consent';

const measurementId = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY as string | undefined;

let initialized = false;

function googleConsentState(c: ConsentChoices) {
  return {
    analytics_storage: c.analytics ? 'granted' : 'denied',
    // No advertising technology is installed on this site, so advertising
    // consent stays denied regardless of choices.
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  };
}

export function initAnalytics() {
  if (!measurementId || initialized || !isProductionHost()) return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];
  // gtag.js only processes dataLayer entries pushed as an `arguments` object,
  // so the global helper must use `arguments` (not a rest array).
  if (!window.gtag) {
    window.gtag = function gtagShim() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }

  // Consent defaults MUST precede 'js'/'config'.
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  gtag('set', 'ads_data_redaction', true);
  const stored = getConsent();
  if (stored.analytics) gtag('consent', 'update', googleConsentState(stored));

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  gtag('js', new Date());
  // Page views are sent manually on route changes (SPA), avoid double counting.
  gtag('config', measurementId, { send_page_view: false });
}

/** Push a Consent Mode update after a visitor changes their choice. */
export function updateGoogleConsent(c: ConsentChoices) {
  if (!initialized) return;
  gtag('consent', 'update', googleConsentState(c));
  if (!c.analytics) clearGoogleAnalyticsCookies();
}

function clearGoogleAnalyticsCookies() {
  const host = window.location.hostname.replace(/^www\./, '');
  document.cookie.split(';').forEach((part) => {
    const name = part.split('=')[0].trim();
    if (name === '_ga' || name.startsWith('_ga_') || name === '_gid') {
      for (const domain of ['', `; domain=.${host}`, `; domain=${window.location.hostname}`]) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
      }
    }
  });
}

export function gtag(...args: unknown[]) {
  if (!measurementId) return;
  window.dataLayer = window.dataLayer || [];
  if (window.gtag) {
    window.gtag(...args);
    return;
  }
  window.dataLayer.push(args);
}

export function trackPageView(path: string) {
  gtag('event', 'page_view', { page_path: path });
}
